import { useState, useEffect } from 'react'
import { Form, Field } from 'react-final-form'
import { useSelector, useDispatch } from 'react-redux'
import { Button, Typography, Box, Stack, CircularProgress, Link } from '@mui/material'
import { loginAccount } from '../actions'
import { ILogin, IReducer } from '../interface'
import { FormattedMessage, useIntl } from 'react-intl'
import TextFieldReduxForm from '../components/Common/TextFieldReduxForm'
import validate from '../validators/validateLogin'
import { useCookies } from 'react-cookie'
import { useLocation, useNavigate } from 'react-router-dom'
import { OpenInNew } from '@mui/icons-material'
import SeoLogin from 'src/components/seo/SeoLogin'

const Login = (props: any) => {
  const [cookies, setCookies] = useCookies(['country_code', 'temporary_user_id', 'popupDay'])
  const dispatch = useDispatch()
  const intl = useIntl()
  const navigate = useNavigate()
  const accountReducer = useSelector((state: IReducer) => state.accountReducer)
  const isAuthenticated = useSelector((state: IReducer) => state.accountReducer.isAuthenticated)
  const lastHistoryAvailable = useSelector((state: IReducer) => state.historyReducer!.lastHistoryAvailable)
  const [openLoading, setOpenLoading] = useState(false)
  const [loginError, setLoginError] = useState(false)
  const [isGonnaRedirect, setIsGonnaRedirect] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState('')
  const location = useLocation()

  useEffect(() => {
    try {
      const params = location.search.substring(1).split('=')
      if (params[0] === 'getBack') {
        setIsGonnaRedirect(true)
        setRedirectUrl(params[1])
      }
    } catch (error) {
      console.error(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      if (isGonnaRedirect) {
        window.location.href = redirectUrl
      } else {
        navigate(lastHistoryAvailable)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.history, isAuthenticated, lastHistoryAvailable])

  const onSubmit = async (values: ILogin) => {
    setOpenLoading(true)
    try {
      dispatch(loginAccount(values, setOpenLoading, setLoginError, setCookies, cookies.popupDay))
    } catch (error) {
      console.error('onSubmit - ', error)
    }
  }

  return (
    <Stack
      sx={{
        minHeight: { xs: 'auto', sm: 'calc(100svh - 60px)' },
        width: '100%',
        p: { xs: 0, sm: 8 },
        alignItems: 'center'
      }}
    >
      <SeoLogin
        title={intl.formatMessage({ id: 'seo.login.title' })}
        description={intl.formatMessage({ id: 'seo.login.description' })}
      />
      <Box
        sx={{
          py: 6,
          m: 'auto',
          bgcolor: '#222',
          p: 4,
          width: '100%',
          maxWidth: { xs: 'auto', sm: 480 },
          borderRadius: { xs: 0, sm: 3 }
        }}
      >
        <Box
          component='img'
          sx={{ width: 40, mb: 3, mt: 1 }}
          src={'https://gemvision.com/Images/stuller-s-c.svg'}
          alt='Stuller'
        />
        <Typography variant='h6'>
          <FormattedMessage id='account.loginToYourStullerAccount' />
        </Typography>
        <Form validate={validate} onSubmit={onSubmit}>
          {({ handleSubmit }) => (
            <form
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                marginTop: '2rem',
                gap: '1.5rem'
              }}
              onSubmit={handleSubmit}
            >
              <Field
                name='username'
                component={TextFieldReduxForm}
                type='text'
                title={intl.formatMessage({ id: 'account.username' })}
                disabled={accountReducer.loggingin || openLoading}
              />
              <Field
                name='password'
                component={TextFieldReduxForm}
                type='password'
                title={intl.formatMessage({ id: 'account.password' })}
                disabled={accountReducer.loggingin || openLoading}
              />

              {loginError && (
                <Button
                  href='https://www.stuller.com/login'
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='contained'
                  color='secondary'
                  sx={{ lineHeight: 1.5, borderRadius: 2 }}
                  endIcon={<OpenInNew />}
                >
                  <FormattedMessage id='account.loginStullerFirst' />
                </Button>
              )}

              <Button
                fullWidth
                type='submit'
                variant='contained'
                color='primary'
                sx={{ textTransform: 'uppercase', minHeight: 60, borderRadius: 2, mt: 2 }}
                disabled={accountReducer.loggingin || openLoading}
                data-cy='login-button'
              >
                {openLoading ? <CircularProgress size={24} /> : <FormattedMessage id='account.logIn' />}
              </Button>
            </form>
          )}
        </Form>
        <Stack direction='row' justifyContent='space-between' flexWrap='wrap' mt={4} gap={3}>
          <Link
            color='secondary'
            href='https://www.stuller.com/accountsignup/'
            target='_blank'
            rel='noopener noreferrer'
            title='Create'
          >
            <FormattedMessage id='account.createAccount' />
          </Link>
          <Link
            color='secondary'
            href='https://www.stuller.com/forgot'
            target='_blank'
            rel='noopener noreferrer'
            title='forgot'
          >
            <FormattedMessage id='account.forgotYourPassword_' />
          </Link>
        </Stack>
      </Box>
    </Stack>
  )
}

export default Login
